// skipcq: JS-W1028, JS-0128
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Rating from '@mui/material/Rating';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import HighlightedText from '../common/HighlightedText';
import BeerProfileShadowBadges from './BeerProfileShadowBadges';
import {
  getSecondaryTaproomLabels,
  resolveBeerCardBreweryDisplay,
} from '../../utils/brewery/getBeerLocationLabels';

/**
 * BeerCard
 * Displays beer information in a card format with ratings and description.
 */
function BeerCard({
  beer,
  styleMap,
  selected,
  onToggleSelect,
  onEdit,
  onShadowProfile,
  shadowProfileSummary,
  breweryNameById,
  groupedBreweryIds,
  hideRatings = false,
}) {
  const {
    name,
    abv,
    styleName,
    style,
    ratings,
    description,
    highlight,
  } = beer;

  const ratingsSafe = ratings || {};
  const displayStyleName =
    styleName ||
    (styleMap && style ? styleMap[style] : null) ||
    style ||
    'Unknown Style';
  const overallRating = ratingsSafe.overall || 0;

  const breweryDisplay = useMemo(
    () => resolveBeerCardBreweryDisplay(beer, breweryNameById, groupedBreweryIds),
    [beer, breweryNameById, groupedBreweryIds],
  );

  const handleCheckboxChange = useCallback(
    (event) => {
      if (onToggleSelect) {
        onToggleSelect(beer.id, event.target.checked);
      }
    },
    [beer.id, onToggleSelect],
  );

  const handleEditClick = useCallback(() => {
    if (onEdit) {
      onEdit(beer);
    }
  }, [beer, onEdit]);

  const handleShadowProfileClick = useCallback(() => {
    if (onShadowProfile) {
      onShadowProfile(beer);
    }
  }, [beer, onShadowProfile]);

  const beerDisplayName = name || 'Unnamed Beer';

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {highlight?.name ? (
              <HighlightedText
                html={highlight.name}
                variant="h6"
                component="div"
                sx={{ mb: 0.5, fontWeight: 'bold', color: '#5D4037' }}
              />
            ) : (
              <Typography
                variant="h6"
                component="div"
                sx={{ mb: 0.5, fontWeight: 'bold', color: '#5D4037' }}
              >
                {beerDisplayName}
              </Typography>
            )}
            {breweryDisplay.showBrandName && breweryDisplay.brandName ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {breweryDisplay.brandName}
              </Typography>
            ) : null}
            {breweryDisplay.locationLabels.map((row) => (
              <Typography
                key={row.breweryId}
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: breweryDisplay.showBrandName ? '0.8125rem' : undefined,
                }}
              >
                {row.label}
              </Typography>
            ))}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mb: 1,
                mt:
                  breweryDisplay.showBrandName ||
                  breweryDisplay.locationLabels.length
                    ? 1
                    : 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {abv ? `${abv}% ABV` : 'N/A ABV'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {displayStyleName}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {hideRatings ? null : (
                <>
                  <Rating
                    value={overallRating}
                    readOnly
                    precision={0.1}
                    size="medium"
                  />
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 'medium', color: 'text.primary' }}
                  >
                    {overallRating.toFixed(1)}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5,
            }}
          >
            {onEdit ? (
              <IconButton
                size="small"
                onClick={handleEditClick}
                aria-label={`Edit beer ${beerDisplayName}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            ) : null}
            {onToggleSelect ? (
              <Checkbox
                checked={selected || false}
                onChange={handleCheckboxChange}
                inputProps={{
                  'aria-label': `Select beer ${beerDisplayName}`,
                }}
              />
            ) : null}
          </Box>
        </Box>

        {description ? (
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            {highlight?.description ? (
              <HighlightedText
                html={highlight.description}
                variant="body2"
                sx={{ color: 'text.secondary' }}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        ) : null}
        {onShadowProfile || shadowProfileSummary?.hasShadow ? (
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <BeerProfileShadowBadges summary={shadowProfileSummary} />
            {onShadowProfile ? (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={handleShadowProfileClick}
                aria-label={`Shadow profile for ${beerDisplayName}`}
              >
                Shadow profile
              </Button>
            ) : null}
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
}

BeerCard.propTypes = {
  beer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    breweryId: PropTypes.string,
    breweryName: PropTypes.string,
    availableBreweryIds: PropTypes.arrayOf(PropTypes.string),
    abv: PropTypes.number,
    styleName: PropTypes.string,
    style: PropTypes.string,
    totalReviews: PropTypes.number,
    ratings: PropTypes.shape({
      appearance: PropTypes.number,
      aroma: PropTypes.number,
      flavor: PropTypes.number,
      mouthfeel: PropTypes.number,
      overall: PropTypes.number,
    }),
    description: PropTypes.string,
    highlight: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
    }),
  }).isRequired,
  styleMap: PropTypes.objectOf(PropTypes.string),
  selected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  onEdit: PropTypes.func,
  onShadowProfile: PropTypes.func,
  shadowProfileSummary: PropTypes.shape({
    hasShadow: PropTypes.bool,
    hasProposedDiffs: PropTypes.bool,
    hasConflicts: PropTypes.bool,
  }),
  breweryNameById: PropTypes.objectOf(PropTypes.string),
  groupedBreweryIds: PropTypes.objectOf(PropTypes.bool),
  hideRatings: PropTypes.bool,
};

BeerCard.defaultProps = {
  styleMap: null,
  selected: false,
  onToggleSelect: null,
  onEdit: null,
  onShadowProfile: null,
  shadowProfileSummary: null,
  breweryNameById: null,
  groupedBreweryIds: null,
  hideRatings: false,
};

export default BeerCard;
export { getSecondaryTaproomLabels };
